import { useState, useEffect, useRef } from 'react';

// Define event names
const BOW_DRAW_START = 'bow-draw-start';
const BOW_DRAW_UPDATE = 'bow-draw-update';
const BOW_DRAW_END = 'bow-draw-end';

// Define event types
interface BowDrawStartEvent extends CustomEvent {
  detail: {
    maxDrawTime: number;
  };
}

interface BowDrawUpdateEvent extends CustomEvent {
  detail: {
    drawStrength: number;
    arrowType: number;
  };
}

interface BowDrawEndEvent extends CustomEvent {
  detail: {
    finalDrawStrength: number;
  };
}

/**
 * Custom hook to track bow drawing state
 * @returns Object containing bow drawing state
 */
export function useBowDrawing() {
  const [isDrawing, setIsDrawing] = useState(false);
  const [drawStrength, setDrawStrength] = useState(0);
  const [arrowType, setArrowType] = useState(0); // Default to normal arrow
  const timeoutRef = useRef<number | null>(null);
  
  // Clear any existing timeout
  const clearDrawTimeout = () => {
    if (timeoutRef.current !== null) {
      window.clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };
  
  useEffect(() => {
    // Handle bow draw start
    const handleDrawStart = (event: Event) => {
      try {
        // Clear any existing timeout to prevent conflicts
        clearDrawTimeout();
        
        const { maxDrawTime } = (event as BowDrawStartEvent).detail;
        setIsDrawing(true);
        setDrawStrength(0);
        
        console.log('[useBowDrawing] Bow draw started, max draw time:', maxDrawTime);
      } catch (error) {
        console.error('[useBowDrawing] Error in handleDrawStart:', error);
      }
    };
    
    // Handle bow draw update
    const handleDrawUpdate = (event: Event) => {
      try {
        // Only update if we're in drawing state to prevent unwanted updates
        if (!isDrawing) {
          setIsDrawing(true); // Ensure drawing state is active if we receive updates
        }
        
        const { drawStrength, arrowType } = (event as BowDrawUpdateEvent).detail;
        setDrawStrength(drawStrength);
        setArrowType(arrowType);
      } catch (error) {
        console.error('[useBowDrawing] Error in handleDrawUpdate:', error);
      }
    };
    
    // Handle bow draw end
    const handleDrawEnd = (event: Event) => {
      try {
        const { finalDrawStrength } = (event as BowDrawEndEvent).detail;
        console.log('[useBowDrawing] Bow draw ended with strength:', finalDrawStrength);
        
        // Clear any existing timeout
        clearDrawTimeout();
        
        // Keep the indicator visible for a moment after release for feedback
        timeoutRef.current = window.setTimeout(() => {
          setIsDrawing(false);
          setDrawStrength(0);
          timeoutRef.current = null;
          console.log('[useBowDrawing] Reset drawing state after timeout');
        }, 500); // Increased from 300ms to 500ms for better visibility
      } catch (error) {
        console.error('[useBowDrawing] Error in handleDrawEnd:', error);
        // Ensure we reset the state even if there's an error
        setIsDrawing(false);
        setDrawStrength(0);
      }
    };
    
    // Add event listeners
    window.addEventListener(BOW_DRAW_START, handleDrawStart);
    window.addEventListener(BOW_DRAW_UPDATE, handleDrawUpdate);
    window.addEventListener(BOW_DRAW_END, handleDrawEnd);
    
    // Clean up
    return () => {
      window.removeEventListener(BOW_DRAW_START, handleDrawStart);
      window.removeEventListener(BOW_DRAW_UPDATE, handleDrawUpdate);
      window.removeEventListener(BOW_DRAW_END, handleDrawEnd);
      clearDrawTimeout();
    };
  }, [isDrawing]); // Add isDrawing as a dependency to ensure event handlers have the latest state
  
  return {
    isDrawing,
    drawStrength,
    arrowType
  };
}

/**
 * Helper functions to dispatch bow drawing events
 * These will be called from the BowController
 */
export const BowDrawingEvents = {
  /**
   * Dispatch event when bow drawing starts
   * @param maxDrawTime Maximum draw time in seconds
   */
  startDrawing: (maxDrawTime: number) => {
    try {
      const event = new CustomEvent(BOW_DRAW_START, {
        detail: { maxDrawTime }
      });
      window.dispatchEvent(event);
      console.log('[BowDrawingEvents] Dispatched BOW_DRAW_START event');
    } catch (error) {
      console.error('[BowDrawingEvents] Error dispatching BOW_DRAW_START event:', error);
    }
  },
  
  /**
   * Dispatch event when bow drawing updates
   * @param drawStrength Current draw strength (0-1)
   * @param arrowType Current arrow type
   */
  updateDrawing: (drawStrength: number, arrowType: number) => {
    try {
      const event = new CustomEvent(BOW_DRAW_UPDATE, {
        detail: { drawStrength, arrowType }
      });
      window.dispatchEvent(event);
    } catch (error) {
      console.error('[BowDrawingEvents] Error dispatching BOW_DRAW_UPDATE event:', error);
    }
  },
  
  /**
   * Dispatch event when bow drawing ends
   * @param finalDrawStrength Final draw strength (0-1)
   */
  endDrawing: (finalDrawStrength: number) => {
    try {
      const event = new CustomEvent(BOW_DRAW_END, {
        detail: { finalDrawStrength }
      });
      window.dispatchEvent(event);
      console.log('[BowDrawingEvents] Dispatched BOW_DRAW_END event');
    } catch (error) {
      console.error('[BowDrawingEvents] Error dispatching BOW_DRAW_END event:', error);
    }
  }
}; 